/**
 * MODO FOCO — el corazón operativo.
 *
 * Una sola tarea a la vez, a pantalla completa, sin navegación normal ni
 * estadísticas. El usuario está entrenando, no administrando la aplicación.
 *
 * DOS INVARIANTES QUE SOSTIENEN TODO:
 *
 * 1. La sesión que se pinta sale SIEMPRE de la base, vía `liveQuery`. No hay
 *    una copia en estado de React que pueda quedar por delante de lo guardado.
 * 2. Cada acción persiste antes de que la interfaz cambie. Android puede matar
 *    el proceso en cualquier momento; guardar al final no es una opción.
 *
 * Las notas y el target son la excepción: se teclean, así que viven en estado
 * local y se guardan al salir del campo o al avanzar. La siembra usa una `ref`
 * con la clave `sesion:paso` en vez de depender del objeto completo — si el
 * efecto se disparara con cada autoguardado, borraría lo que se está
 * escribiendo en ese mismo momento.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  definicionDeSesion,
  etiquetaDeMuestra,
  guardarSesion,
  planDeSesion,
  terminarSesion,
} from '../../app/servicioSesiones';
import { bloqueDeSemana } from '../../curriculum/v1/enfasis';
import { construirPrompt } from '../../curriculum/v1/prompts';
import type { Area } from '../../domain/modelo/areas';
import {
  correccionesVacias,
  type CorreccionesTop3,
  type SesionRegistrada,
} from '../../domain/modelo/sesion';
import { pasoOfreceGrabacion, pasoRequiereGrabacion } from '../../domain/reglas/baseline';
import {
  estadoTrasPracticar,
  materialDeSesion,
  seleccionarParaRetrieval,
  type ResultadoPractica,
} from '../../domain/reglas/retrieval';
import { sesionEnProgreso } from '../../domain/reglas/progresion';
import {
  recogerSenales,
  recomendarFoco,
} from '../../domain/reglas/puntuacionDebilidad';
import { HERRAMIENTA_DE_FOCO } from '../../curriculum/v1/sesiones/d5';
import {
  anotarPaso,
  completarPaso,
  esUltimoPaso,
  iniciarTemporizador,
  omitirPaso,
  pausarTemporizador,
  reiniciarTemporizador,
  resumirSesion,
  retrocederPaso,
} from '../../domain/reglas/sesionActiva';
import { bancoRepo } from '../../persistencia/bancoRepo';
import { grabacionesRepo } from '../../persistencia/grabacionesRepo';
import { sesionesRepo } from '../../persistencia/sesionesRepo';
import { CierreBaseline, dificultadVacia, type DificultadBorrador } from './CierreBaseline';
import { CATEGORIAS_CORRECCION, CierreSesion } from './CierreSesion';
import { FocoDia5 } from './FocoDia5';
import { Grabadora } from './Grabadora';
import { Retrieval } from './Retrieval';
import { HerramientaDelPaso } from './HerramientaDelPaso';
import { Temporizador } from './Temporizador';
import estilos from './ModoFoco.module.css';

export function ModoFoco() {
  const navegar = useNavigate();
  const sesiones = useLiveQuery(() => sesionesRepo.listar(), [], undefined);
  const sesion = sesiones ? sesionEnProgreso(sesiones) : undefined;

  const [notas, setNotas] = useState('');
  const [target, setTarget] = useState('');
  const [confirmandoOmitir, setConfirmandoOmitir] = useState(false);
  const [motivoOmision, setMotivoOmision] = useState('');
  /**
   * `null` = decidilo mirando la sesión.
   *
   * Antes era un booleano en memoria, así que recargar en la pantalla de
   * cierre devolvía al último paso. Ahora se deriva de que todos los pasos
   * estén resueltos, y el estado solo guarda el override explícito:
   * «Seguir en la sesión» (false) o «Terminar la sesión» (true).
   */
  const [cerrandoManual, setCerrandoManual] = useState<boolean | null>(null);
  const [dificultades, setDificultades] = useState<DificultadBorrador[]>([dificultadVacia()]);
  const [autoevaluaciones, setAutoevaluaciones] = useState<Record<string, number>>({});
  const [confianza, setConfianza] = useState<number | null>(null);
  const [guardandoCierre, setGuardandoCierre] = useState(false);
  const [masDificilArea, setMasDificilArea] = useState<Area | null>(null);
  const [masDificilNota, setMasDificilNota] = useState('');
  const [correcciones, setCorrecciones] = useState<CorreccionesTop3>(correccionesVacias());
  const [feedbackCrudo, setFeedbackCrudo] = useState('');
  const [resumenChatGPT, setResumenChatGPT] = useState('');
  const [conPersonaReal, setConPersonaReal] = useState<boolean | null>(null);
  const [usoRespaldoIA, setUsoRespaldoIA] = useState(false);

  const grabaciones = useLiveQuery(
    () => (sesion ? grabacionesRepo.porSesion(sesion.id) : Promise.resolve([])),
    [sesion?.id],
    [],
  );
  const banco = useLiveQuery(() => bancoRepo.listar(), [], undefined);

  /**
   * Fija la instantánea del material al entrar a un paso de retrieval.
   *
   * Escribe UNA vez por paso, guardado por una `ref`: sin ella, `liveQuery`
   * devolvería la sesión actualizada y el efecto volvería a dispararse.
   */
  const materialFijado = useRef<string>('');
  useEffect(() => {
    // `banco === undefined` es «todavía cargando», no «vacío». Marcar la ref
    // antes de que el banco resuelva dejaba la instantánea sin fijar para
    // siempre.
    if (!sesion || banco === undefined) return;
    const clave = `${sesion.id}:${sesion.pasoActual}`;
    if (materialFijado.current === clave) return;
    materialFijado.current = clave;
    void fijarMaterial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesion?.id, sesion?.pasoActual, banco === undefined]);

  const sembrado = useRef<string>('');
  useEffect(() => {
    if (!sesion) return;
    const clave = `${sesion.id}:${sesion.pasoActual}`;
    if (sembrado.current === clave) return;
    sembrado.current = clave;
    setNotas(sesion.pasos[sesion.pasoActual]?.notas ?? '');
    setTarget(sesion.targetActivo ?? '');
  }, [sesion]);

  /**
   * REHIDRATACIÓN DEL CIERRE.
   *
   * Lo que el usuario ya escribió no puede desaparecer por recargar. Una
   * vez por sesión se vuelcan a la interfaz los campos guardados; a partir
   * de ahí manda el estado local, o cada `liveQuery` pisaría lo que se
   * está tecleando.
   */
  const hidratado = useRef<string>('');
  useEffect(() => {
    if (!sesion || hidratado.current === sesion.id) return;
    hidratado.current = sesion.id;
    setAutoevaluaciones({ ...sesion.autoevaluaciones });
    setConfianza(sesion.confianza ?? null);
    setMasDificilArea(sesion.masDificilArea ?? null);
    setMasDificilNota(sesion.masDificil ?? '');
    setCorrecciones(sesion.correccionesTop3 ?? correccionesVacias());
    setFeedbackCrudo(sesion.feedbackChatGPT ?? '');
    setResumenChatGPT(sesion.resumenChatGPT ?? '');
    setConPersonaReal(sesion.conPersonaReal ?? null);
    setUsoRespaldoIA(sesion.usoRespaldoIA ?? false);
    const guardadas = sesion.borradorDificultades;
    setDificultades(guardadas?.length ? guardadas : [dificultadVacia()]);
  }, [sesion]);

  /**
   * Autoguardado de lo tecleado.
   *
   * Guardar solo al salir del campo no basta: si Android mata el proceso
   * mientras se escribe, la nota se pierde y nadie se entera. El retardo evita
   * una escritura por pulsación sin dejar de cumplir «persistir en cada
   * cambio».
   */
  const guardarPendiente = useRef<(() => void) | null>(null);
  guardarPendiente.current = () => {
    if (!sesion) return;
    const conNotas = anotarPaso(sesion, sesion.pasoActual, notas);
    void guardarSesion({ ...conNotas, targetActivo: target });
  };

  useEffect(() => {
    if (!sembrado.current) return;
    const id = setTimeout(() => guardarPendiente.current?.(), 700);
    return () => clearTimeout(id);
  }, [notas, target]);

  /**
   * BORRADOR DEL CIERRE.
   *
   * Se escribe sobre los campos REALES de la sesión, no sobre una copia
   * paralela: una segunda representación del mismo dato acabaría diciendo
   * otra cosa. Escribirlos no completa nada — nada cuenta como hecho hasta
   * que `estado` sea `completada` o `parcial`, y la siembra del Learning
   * Bank ocurre solo al confirmar el cierre.
   *
   * Las escrituras se ENCADENAN, nunca corren en paralelo. Cada una hace
   * leer-modificar-escribir sobre la misma fila; dos a la vez pueden leer el
   * mismo estado, y la segunda pisa lo que escribió la primera. Una
   * puntuación desaparecía de vez en cuando, y solo a veces. Con la cadena,
   * la siguiente siempre lee lo que dejó la anterior.
   */
  const cola = useRef<Promise<void>>(Promise.resolve());

  const borradorPendiente = useRef<(() => void) | null>(null);
  borradorPendiente.current = () => {
    if (!sesion) return;
    const id = sesion.id;
    cola.current = cola.current.then(async () => {
      // Un fallo NO puede romper la cadena para siempre: sin este catch, una
      // escritura fallida dejaría el autoguardado muerto el resto de la
      // sesión sin que nadie se entere. Se aísla acá y se vuelve a lanzar
      // fuera de la cadena, para que el aviso global lo muestre igual.
      try {
        // RELEER antes de escribir. La copia del render puede ir por detrás
        // —`liveQuery` entrega de forma asíncrona— y guardar el borrador con
        // ella pisaba la instantánea del Día 4 recién fijada. Bug real,
        // encontrado por el test crítico del Día 4.
        const actual = await sesionesRepo.obtener(id);
        if (!actual) return;
        await guardarSesion({
          ...actual,
          autoevaluaciones: autoevaluaciones as SesionRegistrada['autoevaluaciones'],
          ...(confianza !== null ? { confianza } : {}),
          ...(masDificilArea ? { masDificilArea } : {}),
          masDificil: masDificilNota,
          correccionesTop3: correcciones,
          feedbackChatGPT: feedbackCrudo,
          resumenChatGPT,
          ...(conPersonaReal !== null ? { conPersonaReal, usoRespaldoIA } : {}),
          borradorDificultades: dificultades,
        });
      } catch (error) {
        setTimeout(() => {
          throw error;
        });
      }
    });
  };

  /**
   * El borrador se ARMA después de hidratar, no antes.
   *
   * Los efectos corren una primera vez con los valores recién volcados de
   * la base. Esa escritura no aporta nada —es lo mismo que ya está
   * guardado— y sí puede hacer daño: llega tarde y pisa lo que otra acción
   * escribió mientras tanto. Bug real, encontrado por los tests críticos
   * del Día 4 y del Día 5.
   */
  const armadoInmediato = useRef<string>('');
  const armadoDiferido = useRef<string>('');
  function armado(bandera: { current: string }): boolean {
    if (!sesion) return false;
    if (bandera.current === sesion.id) return true;
    bandera.current = sesion.id;
    return false;
  }

  // Inmediato para lo que se elige de un toque: un radio o un chip no se
  // vuelve a pulsar, así que no hay nada que agrupar.
  useEffect(() => {
    if (!armado(armadoInmediato)) return;
    borradorPendiente.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoevaluaciones, confianza, masDificilArea, conPersonaReal, usoRespaldoIA]);

  // Con retardo para lo que se teclea, por la misma razón que las notas:
  // una escritura por pulsación sobra, y perder el texto no.
  useEffect(() => {
    if (!armado(armadoDiferido)) return;
    const id = setTimeout(() => borradorPendiente.current?.(), 700);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masDificilNota, correcciones, feedbackCrudo, resumenChatGPT, dificultades]);

  if (sesiones === undefined) {
    return <div className={estilos.cargando}>Cargando…</div>;
  }

  if (!sesion) {
    return (
      <div className={estilos.vacio}>
        <p>No hay ninguna sesión en curso.</p>
        <button type="button" className={estilos.principal} onClick={() => navegar('/')}>
          Volver a Hoy
        </button>
      </div>
    );
  }

  /**
   * Estar en el cierre es un HECHO de la sesión, no un estado de React:
   * todos los pasos resueltos significa que ya no queda nada que hacer.
   * Así recargar en el cierre vuelve al cierre.
   */
  const todoResuelto =
    sesion.pasos.length > 0 &&
    sesion.pasos.every((paso) => paso.completado || paso.omitido);
  const cerrando = cerrandoManual ?? todoResuelto;

  const definicion = definicionDeSesion(sesion);
  const plan = planDeSesion(sesion);
  const pasoCurriculum = plan.pasos[sesion.pasoActual]!;
  const pasoRegistrado = sesion.pasos[sesion.pasoActual]!;
  const total = plan.pasos.length;
  const completados = sesion.pasos.filter((p) => p.completado || p.omitido).length;
  const ultimo = esUltimoPaso(sesion);
  const esBaseline = sesion.tipo === 'baseline';
  /** Baseline y checkpoints son las cuatro mediciones comparables del programa. */
  const esMedicion = esBaseline || sesion.tipo === 'checkpoint';

  // Un paso que exige muestra NO puede darse por completado sin ella. Solo se
  // puede omitir, y omitir deja la sesión parcial — que es lo honesto.
  const exigeMuestra = pasoRequiereGrabacion(pasoCurriculum);
  const ofreceMuestra = pasoOfreceGrabacion(pasoCurriculum);
  const tieneMuestra = grabaciones.some((g) => g.stepKey === pasoCurriculum.clave);
  const faltaMuestra = exigeMuestra && !tieneMuestra;

  // Material del Día 4. La instantánea se fija al entrar al paso y no se
  // recalcula: si el banco cambia a mitad de sesión, lo de hoy no se mueve.
  const pideElementos = pasoCurriculum.elementosBanco;
  const idsSeleccionados = sesion.elementosSeleccionados ?? [];
  const materialDeHoy = materialDeSesion(banco ?? [], idsSeleccionados);
  const resultadosPractica = sesion.practicaElementos ?? {};

  // ── Día 5 ────────────────────────────────────────────────────────────
  const eligeFoco = pasoCurriculum.eligeFoco === true;
  const focoElegido = sesion.focoDia5 ?? null;

  // Las señales se recogen de los datos REALES de la semana. El baseline no
  // entra: sus dificultades son material, no señal de esta semana.
  const senales = recogerSenales({
    sesiones: sesiones ?? [],
    banco: banco ?? [],
    semana: sesion.semana,
    areasDelBloque: bloqueDeSemana(sesion.semana)?.areas ?? [],
  });
  const recomendacion = recomendarFoco(senales);

  /**
   * El paso «según tu foco» se resuelve a una herramienta CONCRETA.
   *
   * Es la capa ligera: mismo paso, distinta herramienta. Sin foco elegido
   * todavía, no se muestra ninguna — mostrar las cuatro sería justo lo que la
   * especificación pide evitar.
   */
  const pasoResuelto =
    pasoCurriculum.herramienta === 'segunFoco'
      ? {
          ...pasoCurriculum,
          herramienta: focoElegido ? HERRAMIENTA_DE_FOCO[focoElegido] : ('ninguna' as const),
        }
      : pasoCurriculum;

  const prompt = pasoCurriculum.prompt
    ? construirPrompt(pasoCurriculum.prompt, {
        enfasis: bloqueDeSemana(sesion.semana),
        foco: sesion.focoDia5 ?? null,
        // Solo el material de ESTA sesión, y solo el ya revelado: el prompt no
        // puede adelantar respuestas si el orden curricular pone el retrieval
        // local primero.
        targets: [
          ...(sesion.targetActivo ? [sesion.targetActivo] : []),
          ...materialDeHoy
            .filter((e) => resultadosPractica[e.id])
            .map((e) => e.correccionTarget.trim() || e.contextoOriginal.trim()),
        ],
      })
    : null;

  /**
   * Fija el foco del Día 5.
   *
   * Guarda también QUÉ recomendó la aplicación y si el usuario eligió otra
   * cosa: es la única forma de saber después si la recomendación ayuda.
   *
   * Una vez fijado no se recalcula en toda la sesión.
   */
  async function elegirFoco(area: Area) {
    if (!sesion) return;
    const recomendada =
      recomendacion.tipo === 'sugerencia' ? recomendacion.mejor.area : undefined;
    await guardarSesion({
      ...sesion,
      focoDia5: area,
      focoRecomendado: recomendada,
      focoManual: recomendada !== undefined && recomendada !== area,
    });
  }

  /**
   * Fija la instantánea del material la primera vez que se entra al paso.
   *
   * Se llama desde el render porque depende del paso actual, pero está
   * guardada por `idsSeleccionados.length === 0`: escribe una sola vez.
   */
  async function fijarMaterial() {
    if (!sesion || !pideElementos || idsSeleccionados.length > 0) return;
    const elegidos = seleccionarParaRetrieval(banco ?? [], pideElementos.maximo);
    if (elegidos.length === 0) return;
    await guardarSesion({ ...sesion, elementosSeleccionados: elegidos.map((e) => e.id) });
  }

  /**
   * Registra la práctica de un elemento.
   *
   * `ultimaPracticaEn` se actualiza AQUÍ, al practicar de verdad — no al abrir
   * el Día 4 ni al seleccionarlo. Y nunca se marca `dominado` solo, que es una
   * decisión manual.
   */
  async function practicarElemento(id: string, resultado: ResultadoPractica) {
    if (!sesion) return;
    await guardarSesion({
      ...sesion,
      practicaElementos: { ...(sesion.practicaElementos ?? {}), [id]: resultado },
    });
    const elemento = (banco ?? []).find((e) => e.id === id);
    if (!elemento) return;
    await bancoRepo.guardar({
      ...elemento,
      estado: estadoTrasPracticar(elemento.estado, resultado),
      ultimaPracticaEn: new Date().toISOString(),
    });
  }

  /** Toda mutación pasa por aquí: transformar y persistir, en ese orden. */
  async function aplicar(transformar: (s: SesionRegistrada) => SesionRegistrada) {
    if (!sesion) return;
    await guardarSesion(transformar(sesion));
  }

  async function guardarNotasYTarget() {
    if (!sesion) return;
    let siguiente = anotarPaso(sesion, sesion.pasoActual, notas);
    siguiente = { ...siguiente, targetActivo: target };
    await guardarSesion(siguiente);
  }

  async function completar() {
    // Guardar notas y target primero, releer, y recién entonces avanzar: así el
    // avance parte de lo que quedó escrito, no de una copia en memoria.
    await guardarNotasYTarget();
    const actual = await sesionesRepo.obtener(sesion!.id);
    if (!actual) return;
    await guardarSesion(completarPaso(actual, actual.pasoActual));
    if (ultimo) setCerrandoManual(true);
  }

  async function omitir() {
    await guardarNotasYTarget();
    const actual = await sesionesRepo.obtener(sesion!.id);
    if (!actual) return;
    await guardarSesion(omitirPaso(actual, actual.pasoActual, motivoOmision));
    setConfirmandoOmitir(false);
    setMotivoOmision('');
    if (ultimo) setCerrandoManual(true);
  }

  /**
   * Siembra el Learning Bank con las dificultades CONFIRMADAS.
   *
   * Solo las que tienen texto: nada se crea automáticamente y nadie está
   * obligado a llenar casillas.
   */
  async function sembrarBanco() {
    if (!sesion) return;
    const reales = dificultades.filter((d) => d.contexto.trim().length > 0);
    for (const dificultad of reales) {
      // `sembrar` es idempotente por `origenClave`: volver a guardar el cierre
      // actualiza el mismo elemento en vez de crear un duplicado.
      await bancoRepo.sembrar({
        // La identidad sale del id ESTABLE de la dificultad, no de su
        // posición: editarla o reordenarla no crea otro elemento.
        origenClave: `${sesion.id}:dificultad:${dificultad.id}`,
        kind: dificultad.kind,
        area: dificultad.area,
        contextoOriginal: dificultad.contexto.trim(),
        correccionTarget: dificultad.correccionTarget.trim(),
        fuente: 'baseline',
        prioridad: dificultad.prioridad,
      });
    }
  }

  /**
   * Lleva al Learning Bank las correcciones que el usuario marcó.
   *
   * Idempotente por `origenClave`: volver atrás y guardar otra vez actualiza
   * las mismas, nunca crea copias. Una corrección sin texto, o marcada como no
   * confiable, no crea nada.
   */
  async function sembrarCorrecciones() {
    if (!sesion) return;
    for (const { clave, area } of CATEGORIAS_CORRECCION) {
      const correccion = correcciones[clave];
      if (!correccion.alBanco) continue;
      if (correccion.noConfiable) continue;
      if (!correccion.texto.trim()) continue;

      await bancoRepo.sembrar({
        origenClave: `${sesion.id}:correccion:${clave}`,
        kind: 'error',
        area,
        // El contexto solo existe si el usuario lo escribió. Vacío es
        // legítimo: preferible a inventar una situación que no confirmó.
        contextoOriginal: (correccion.contexto ?? '').trim(),
        // La corrección ES la respuesta. Si viviera en `contextoOriginal`,
        // el Día 4 la enseñaría antes de que el usuario intente nada.
        correccionTarget: correccion.texto.trim(),
        fuente: 'chatgpt',
      });
    }
  }

  async function terminar() {
    if (!sesion) return;
    // Releer antes de cerrar. La copia del render puede ir por detrás de lo
    // persistido —`liveQuery` entrega de forma asíncrona— y cerrar con ella
    // marcaba «parcial» una sesión que sí estaba completa.
    setGuardandoCierre(true);
    try {
      if (esBaseline) await sembrarBanco();
      await sembrarCorrecciones();
      const actual = await sesionesRepo.obtener(sesion.id);
      const base = actual ?? sesion;
      await terminarSesion({
        ...base,
        autoevaluaciones: { ...base.autoevaluaciones, ...autoevaluaciones },
        ...(confianza !== null ? { confianza } : {}),
        ...(masDificilArea ? { masDificilArea } : {}),
        ...(masDificilNota.trim() ? { masDificil: masDificilNota.trim() } : {}),
        ...(definicion.capturaCorrecciones ? { correccionesTop3: correcciones } : {}),
        ...(feedbackCrudo.trim() ? { feedbackChatGPT: feedbackCrudo.trim() } : {}),
        ...(resumenChatGPT.trim() ? { resumenChatGPT: resumenChatGPT.trim() } : {}),
        ...(conPersonaReal !== null ? { conPersonaReal, usoRespaldoIA } : {}),
        grabacionIds: grabaciones.map((g) => g.id),
      });
      navegar('/', { replace: true });
    } finally {
      setGuardandoCierre(false);
    }
  }

  if (cerrando) {
    const resumen = resumirSesion(sesion);
    const minutos = Math.round(resumen.segundos / 60);
    return (
      <div className={estilos.contenedor}>
        <div className={estilos.cierre}>
          <h1 className={estilos.tituloCierre}>{definicion.titulo}</h1>
          <p className={estilos.subtituloCierre}>Sesión terminada</p>

          <ul className={estilos.resumen}>
            <li>
              <strong>{resumen.completados}</strong> de {resumen.total} pasos completados
            </li>
            {resumen.omitidos > 0 ? (
              <li>
                <strong>{resumen.omitidos}</strong> omitidos
              </li>
            ) : null}
            {resumen.sinTocar > 0 ? (
              <li>
                <strong>{resumen.sinTocar}</strong> sin hacer
              </li>
            ) : null}
            <li>
              <strong>{minutos}</strong> minutos aproximados
            </li>
          </ul>

          <p className={estilos.estado}>
            Va a quedar registrada como{' '}
            <strong>
              {resumen.estadoPropuesto === 'completada'
                ? 'completada'
                : resumen.estadoPropuesto === 'parcial'
                  ? 'parcial'
                  : 'abandonada'}
            </strong>
            .{' '}
            {resumen.estadoPropuesto !== 'completada'
              ? 'Lo que omitiste no cuenta como hecho, y eso está bien: el registro tiene que ser honesto para que las recomendaciones sirvan.'
              : ''}
          </p>

          {sesion.pasos.some((p) => p.notas?.trim()) ? (
            <div className={estilos.notasResumen}>
              <h2 className={estilos.subtitulo}>Tus notas</h2>
              {sesion.pasos.map((paso, i) =>
                paso.notas?.trim() ? (
                  <p key={paso.stepKey}>
                    <strong>{plan.pasos[i]?.titulo}:</strong> {paso.notas}
                  </p>
                ) : null,
              )}
            </div>
          ) : null}

          {/*
            El baseline es la semilla del programa: acá se registran las
            dificultades reales y la autoevaluación que servirá para comparar
            contra W4, W8 y W12.
          */}
          {esBaseline ? (
            <CierreBaseline
              dificultades={dificultades}
              onCambiarDificultades={setDificultades}
            />
          ) : null}

          <CierreSesion
            areasAEvaluar={definicion.areasAEvaluar}
            autoevaluaciones={autoevaluaciones}
            onCambiarAutoevaluacion={(area, valor) =>
              setAutoevaluaciones((previas) => ({ ...previas, [area]: valor }))
            }
            confianza={confianza}
            onCambiarConfianza={setConfianza}
            masDificilArea={masDificilArea}
            onCambiarMasDificilArea={setMasDificilArea}
            masDificilNota={masDificilNota}
            onCambiarMasDificilNota={setMasDificilNota}
            correcciones={definicion.capturaCorrecciones ? correcciones : null}
            onCambiarCorreccion={(cual, cambios) =>
              setCorrecciones((previas) => ({
                ...previas,
                [cual]: { ...previas[cual], ...cambios },
              }))
            }
            feedbackCrudo={feedbackCrudo}
            onCambiarFeedbackCrudo={setFeedbackCrudo}
            pideResumen={esMedicion}
            resumenChatGPT={resumenChatGPT}
            onCambiarResumenChatGPT={setResumenChatGPT}
            pidePersonaReal={plan.pasos.some((paso) => paso.registraPersonaReal)}
            conPersonaReal={conPersonaReal}
            onCambiarConPersonaReal={setConPersonaReal}
            usoRespaldoIA={usoRespaldoIA}
            onCambiarUsoRespaldoIA={setUsoRespaldoIA}
          />

          <button
            type="button"
            className={estilos.principal}
            onClick={() => void terminar()}
            disabled={guardandoCierre}
          >
            {guardandoCierre ? 'Guardando…' : 'Guardar y volver a Hoy'}
          </button>
          <button
            type="button"
            className={estilos.secundario}
            onClick={() => setCerrandoManual(false)}
          >
            Seguir en la sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={estilos.contenedor}>
      <header className={estilos.cabecera}>
        <div>
          <p className={estilos.sesion}>{definicion.titulo}</p>
          <p className={estilos.contador}>
            Paso {sesion.pasoActual + 1} de {total}
          </p>
        </div>
        <button
          type="button"
          className={estilos.salir}
          onClick={() => setCerrandoManual(true)}
          aria-label="Terminar la sesión"
        >
          Terminar
        </button>
      </header>

      <div
        className={estilos.progreso}
        role="progressbar"
        aria-valuenow={completados}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label="Progreso de la sesión"
      >
        <div
          className={estilos.progresoRelleno}
          style={{ width: `${(completados / total) * 100}%` }}
        />
      </div>

      <main className={estilos.cuerpo}>
        <h1 className={estilos.titulo}>{pasoCurriculum.titulo}</h1>
        <p className={estilos.tiempoSugerido}>
          {pasoCurriculum.minutosSugeridos === null
            ? 'Sin tiempo sugerido'
            : `${pasoCurriculum.minutosSugeridos} min sugeridos`}
        </p>

        <p className={estilos.objetivo}>{pasoCurriculum.objetivo}</p>

        <details className={estilos.porQue}>
          <summary>¿Por qué hacemos esto?</summary>
          <p>{pasoCurriculum.porQue}</p>
        </details>

        <ol className={estilos.instrucciones}>
          {pasoCurriculum.instrucciones.map((instruccion) => (
            <li key={instruccion}>{instruccion}</li>
          ))}
        </ol>

        {pasoCurriculum.notaTiempo ? (
          <p className={estilos.notaTiempo}>{pasoCurriculum.notaTiempo}</p>
        ) : null}

        {faltaMuestra ? (
          <p className={estilos.faltaMuestra} role="status">
            Este paso necesita una muestra de audio para darse por hecho. Grabala o
            cargá un archivo. Si hoy no podés, omitilo: la sesión seguirá, pero quedará
            como parcial y el baseline no contará como completo.
          </p>
        ) : null}

        <HerramientaDelPaso
          paso={pasoResuelto}
          prompt={prompt}
          target={target}
          onCambiarTarget={setTarget}
          fuenteListeningClave={sesion.fuenteListeningClave}
          onAntesDeSalir={async () => {
            // Persistir antes de salir: puede que no volvamos a este proceso.
            await guardarNotasYTarget();
          }}
        />

        {eligeFoco ? (
          <FocoDia5
            recomendacion={recomendacion}
            focoElegido={focoElegido}
            onElegir={(area) => void elegirFoco(area)}
          />
        ) : null}

        {pideElementos ? (
          <Retrieval
            elementos={materialDeHoy}
            resultados={resultadosPractica}
            onPracticar={(id, resultado) => void practicarElemento(id, resultado)}
            minimo={pideElementos.minimo}
          />
        ) : null}

        {ofreceMuestra ? (
          <Grabadora
            sesionId={sesion.id}
            stepKey={pasoCurriculum.clave}
            etiqueta={etiquetaDeMuestra(sesion)}
            nombreSugerido={`${definicion.corto} · ${pasoCurriculum.titulo}`}
            minutosSugeridos={pasoCurriculum.minutosSugeridos}
          />
        ) : null}

        <Temporizador
          paso={pasoRegistrado}
          minutosSugeridos={pasoCurriculum.minutosSugeridos}
          onIniciar={() => void aplicar((s) => iniciarTemporizador(s, s.pasoActual))}
          onPausar={() => void aplicar((s) => pausarTemporizador(s, s.pasoActual))}
          onReiniciar={() => void aplicar((s) => reiniciarTemporizador(s, s.pasoActual))}
        />

        <div className={estilos.notas}>
          <label className={estilos.etiqueta} htmlFor="notas-paso">
            Notas de este paso
          </label>
          <textarea
            id="notas-paso"
            className={estilos.campoNotas}
            value={notas}
            onChange={(evento) => setNotas(evento.target.value)}
            onBlur={() => void guardarNotasYTarget()}
            rows={3}
            placeholder="lo que quieras recordar"
          />
        </div>
      </main>

      {confirmandoOmitir ? (
        <div className={estilos.confirmacion} role="dialog" aria-label="Omitir este paso">
          <p className={estilos.confirmacionTexto}>
            ¿Omitir este paso? No va a contar como hecho.
          </p>
          <input
            className={estilos.campo}
            value={motivoOmision}
            onChange={(evento) => setMotivoOmision(evento.target.value)}
            placeholder="motivo (opcional)"
            aria-label="Motivo de la omisión"
          />
          <div className={estilos.acciones}>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => setConfirmandoOmitir(false)}
            >
              Cancelar
            </button>
            <button type="button" className={estilos.principal} onClick={() => void omitir()}>
              Omitir
            </button>
          </div>
        </div>
      ) : null}

      <footer className={estilos.pie}>
        <button
          type="button"
          className={estilos.secundario}
          onClick={() => void aplicar((s) => retrocederPaso(s))}
          disabled={sesion.pasoActual === 0}
        >
          Atrás
        </button>
        <button
          type="button"
          className={estilos.secundario}
          onClick={() => setConfirmandoOmitir(true)}
        >
          Omitir
        </button>
        <button
          type="button"
          className={estilos.principal}
          onClick={() => void completar()}
          disabled={faltaMuestra}
          title={faltaMuestra ? 'Este paso necesita una muestra de audio' : undefined}
        >
          {ultimo ? 'Terminar sesión' : 'Terminé este paso'}
        </button>
      </footer>
    </div>
  );
}
